import { type CanvasSize } from '../CanvasSize';
import { type Viewport } from '../Viewport';

const MIN_EXTENT = 1;

/** A rectangle in physical pixels with the origin at the bottom left, the way WebGL wants it. */
export interface WebGLRect {
  /** Distance from the drawing buffer's left edge. */
  readonly x: number;
  /** Distance from the drawing buffer's bottom edge. */
  readonly y: number;
  /** Width in physical pixels. */
  readonly width: number;
  /** Height in physical pixels. */
  readonly height: number;
}

/**
 * Converts a {@link Viewport} into the rectangle WebGL's viewport and scissor calls expect.
 *
 * Two conventions differ across this boundary and both are handled here: a viewport is measured in CSS
 * pixels from the canvas' top left, because that is what Pointer Events report, while WebGL wants
 * physical pixels from the bottom left. Keeping the conversion in one pure function is what lets it be
 * tested without a GL context.
 *
 * Each of the four edges is rounded, and the extents are derived from the rounded edges. Rounding the
 * offset and the extent independently would leave a pixel unaccounted for on some sizes, which shows up
 * as a hairline seam between the playfield and a letterbox bar.
 */
export function toWebGLRect(size: CanvasSize, viewport: Viewport): WebGLRect {
  const ratio = size.pixelRatio;
  const left = Math.round(viewport.offsetX * ratio);
  const right = Math.round((viewport.offsetX + viewport.width) * ratio);
  const top = Math.round(viewport.offsetY * ratio);
  const bottom = Math.round((viewport.offsetY + viewport.height) * ratio);

  return {
    x: left,
    y: Math.max(0, size.pixelHeight - bottom),
    width: Math.max(MIN_EXTENT, right - left),
    height: Math.max(MIN_EXTENT, bottom - top),
  };
}
